import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import relatorioService, { type PrestacaoBem } from '../../services/relatorioPrestacaoContasService';
import DownloadButtons from './DownloadButtons';
import PrestacaoPcArquivos from './PrestacaoPcArquivos';
import { toUpperText } from '../../utils/formatters';
import {
  bodyTextClass, dangerTextButtonClass, formatCurrency, formatCurrencyInput, formatDate,
  inputClass, labelTextClass, parseCurrencyInput, primaryButtonClass, strongTextClass,
  subtleSurfaceClass, surfaceClass, type SectionProps,
} from './shared';

type Props = SectionProps & { situacao: 'ADQUIRIDO' | 'DEVOLVIDO' };

const formDefaults = { documentoNumero: '', tombamento: '', documentoTipo: '', dataDocumento: '', especificacao: '', quantidade: '1', valorUnitario: '' };

const PrestacaoBensSection: React.FC<Props> = ({ pcId, podeEditar, situacao, onAlterado }) => {
  const { user } = useAuth();
  const role = String((user as any)?.role || (user as any)?.perfil || '').toUpperCase();
  const isConveniosOuAdmin = role === 'CONVENIOS' || role === 'ADMIN';

  const [bens, setBens] = useState<PrestacaoBem[]>([]);
  const [form, setForm] = useState(formDefaults);
  const [devolucaoData, setDevolucaoData] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Anexo 4 mostra TODOS os bens da parcela (com status), nao so os ainda adquiridos --
  // o bem continua tendo sido "adquirido" mesmo depois de devolvido. Anexo 7 mostra
  // so os ja devolvidos, igual ao modelo oficial do termo de devolucao.
  const carregar = async () => {
    setLoading(true);
    setError(null);
    try {
      setBens(await relatorioService.getBens(pcId, situacao === 'DEVOLVIDO' ? 'DEVOLVIDO' : undefined));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar os bens.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId, situacao]);

  const criar = async () => {
    if (!form.documentoNumero || !form.tombamento || !form.especificacao || !form.valorUnitario) {
      setError('Informe o número do documento, o tombamento, a especificação e o valor unitário.');
      return;
    }
    setSaving('novo');
    setError(null);
    try {
      await relatorioService.criarBem(pcId, {
        documentoNumero: form.documentoNumero,
        tombamento: form.tombamento,
        documentoTipo: form.documentoTipo || undefined,
        dataDocumento: form.dataDocumento || null,
        especificacao: form.especificacao,
        quantidade: form.quantidade ? Number(form.quantidade) : null,
        valorUnitario: parseCurrencyInput(form.valorUnitario),
      });
      setForm(formDefaults);
      await carregar();
      onAlterado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao lançar o bem.');
    } finally {
      setSaving(null);
    }
  };

  const excluir = async (bemId: string) => {
    if (!confirm('Excluir este bem?')) return;
    setSaving(bemId);
    try {
      await relatorioService.excluirBem(bemId);
      await carregar();
      onAlterado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao excluir o bem.');
    } finally {
      setSaving(null);
    }
  };

  const devolver = async (bemId: string) => {
    const data = devolucaoData[bemId];
    if (!data) {
      setError('Informe a data de devolução.');
      return;
    }
    setSaving(bemId);
    setError(null);
    try {
      await relatorioService.marcarDevolucao(bemId, data);
      await carregar();
      onAlterado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao registrar a devolução.');
    } finally {
      setSaving(null);
    }
  };

  const solicitarDevolucao = async (bemId: string) => {
    setSaving(bemId);
    setError(null);
    try {
      await relatorioService.solicitarDevolucao(bemId);
      await carregar();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao solicitar a devolução.');
    } finally {
      setSaving(null);
    }
  };

  const statusBadge = (b: PrestacaoBem) => {
    if (b.situacao === 'DEVOLVIDO') {
      return (
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
          Devolvido em {formatDate(b.dataDevolucao)}
        </span>
      );
    }
    if (b.devolucaoSolicitadaEm) {
      return (
        <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30">
          Devolução solicitada em {formatDate(b.devolucaoSolicitadaEm)}
        </span>
      );
    }
    return (
      <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30">
        Adquirido
      </span>
    );
  };

  const titulo = situacao === 'DEVOLVIDO' ? 'Anexo 7 · Termo de Devolução de Bens Adquiridos' : 'Anexo 4 · Relação de Bens Adquiridos';
  const anexo = situacao === 'DEVOLVIDO' ? '7' : '4';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className={`text-lg font-semibold ${strongTextClass}`}>{titulo}</h2>
        <DownloadButtons pcId={pcId} anexo={anexo} nomeArquivo={`anexo-${anexo}-bens`} comXlsx={situacao === 'ADQUIRIDO'} />
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {situacao === 'ADQUIRIDO' && podeEditar && (
        <div className={`space-y-3 rounded-2xl border p-4 ${subtleSurfaceClass}`}>
          <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${labelTextClass}`}>Lançar bem</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <input className={inputClass} placeholder="Nº do documento" value={form.documentoNumero}
              onChange={(e) => setForm((p) => ({ ...p, documentoNumero: toUpperText(e.target.value) }))} />
            <input className={inputClass} placeholder="Tombamento (nº de patrimônio)" value={form.tombamento}
              onChange={(e) => setForm((p) => ({ ...p, tombamento: toUpperText(e.target.value) }))} />
            <input className={inputClass} placeholder="Tipo (nota fiscal, recibo...)" value={form.documentoTipo}
              onChange={(e) => setForm((p) => ({ ...p, documentoTipo: toUpperText(e.target.value) }))} />
            <input type="date" className={inputClass} value={form.dataDocumento}
              onChange={(e) => setForm((p) => ({ ...p, dataDocumento: e.target.value }))} />
            <input className={`sm:col-span-2 ${inputClass}`} placeholder="Especificação: descrição, modelo, marca, nº de série ou placa" value={form.especificacao}
              onChange={(e) => setForm((p) => ({ ...p, especificacao: toUpperText(e.target.value) }))} />
            <input type="number" step="0.01" className={inputClass} placeholder="Quantidade" value={form.quantidade}
              onChange={(e) => setForm((p) => ({ ...p, quantidade: e.target.value }))} />
            <input type="text" inputMode="numeric" className={inputClass} placeholder="Valor unitário (R$ 0,00)" value={form.valorUnitario}
              onChange={(e) => setForm((p) => ({ ...p, valorUnitario: formatCurrencyInput(e.target.value) }))} />
          </div>
          <button type="button" onClick={criar} disabled={saving === 'novo'}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${primaryButtonClass}`}>
            {saving === 'novo' ? 'Salvando...' : 'Adicionar bem'}
          </button>
        </div>
      )}

      {loading ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : bens.length === 0 ? (
        <p className={`text-sm ${labelTextClass}`}>
          {situacao === 'DEVOLVIDO' ? 'Não foram adquiridos bens permanentes.' : 'Nenhum bem lançado nesta PC.'}
        </p>
      ) : (
        <div className={`overflow-x-auto rounded-2xl border ${surfaceClass}`}>
          <table className="w-full text-sm">
            <thead className={subtleSurfaceClass}>
              <tr>
                <th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Documento</th>
                <th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Tombamento</th>
                <th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Especificação</th>
                <th className={`px-4 py-2 text-right font-semibold ${labelTextClass}`}>Qtd.</th>
                <th className={`px-4 py-2 text-right font-semibold ${labelTextClass}`}>Valor total</th>
                {situacao === 'ADQUIRIDO' && <th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Status</th>}
                <th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}></th>
              </tr>
            </thead>
            <tbody>
              {bens.map((b) => (
                <tr key={b.id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className={`px-4 py-2 ${bodyTextClass}`}>{b.documentoNumero} {b.documentoTipo ? `(${b.documentoTipo})` : ''}</td>
                  <td className={`px-4 py-2 ${bodyTextClass}`}>{b.tombamento || '-'}</td>
                  <td className={`px-4 py-2 ${strongTextClass}`}>
                    {b.especificacao || '-'}
                    {situacao === 'ADQUIRIDO' && (
                      <div className="mt-2">
                        <p className={`mb-1 text-xs font-normal ${labelTextClass}`}>
                          Foto com o tombamento aparente (veículo: adesivado).
                        </p>
                        <PrestacaoPcArquivos pcId={pcId} tipo="FOTO_BEM" bemId={b.id}
                          podeEditar={podeEditar && b.situacao === 'ADQUIRIDO'} accept="image/*" botao="Anexar foto" onChange={onAlterado} />
                      </div>
                    )}
                  </td>
                  <td className={`px-4 py-2 text-right ${bodyTextClass}`}>{b.quantidade ?? '-'}</td>
                  <td className={`px-4 py-2 text-right ${bodyTextClass}`}>{formatCurrency(b.valorTotal)}</td>
                  {situacao === 'ADQUIRIDO' && <td className="px-4 py-2">{statusBadge(b)}</td>}
                  <td className="px-4 py-2 text-right">
                    {situacao === 'ADQUIRIDO' && b.situacao === 'ADQUIRIDO' && podeEditar && (
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {isConveniosOuAdmin && !b.devolucaoSolicitadaEm && (
                          <button type="button" onClick={() => solicitarDevolucao(b.id)} disabled={saving === b.id}
                            className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                            Solicitar devolução
                          </button>
                        )}
                        <input type="date" className="rounded-lg border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900"
                          value={devolucaoData[b.id] || ''}
                          onChange={(e) => setDevolucaoData((prev) => ({ ...prev, [b.id]: e.target.value }))} />
                        <button type="button" onClick={() => devolver(b.id)} disabled={saving === b.id}
                          className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                          Devolver
                        </button>
                        <button type="button" onClick={() => excluir(b.id)} disabled={saving === b.id} className={dangerTextButtonClass}>
                          Excluir
                        </button>
                      </div>
                    )}
                    {situacao === 'DEVOLVIDO' && (
                      <span className={`text-xs ${labelTextClass}`}>Devolvido em {formatDate(b.dataDevolucao)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PrestacaoBensSection;
